import moment from "moment-timezone";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import socket from "../../../api/socket";
import { EventProps, SlotGroupPropGetter, Views } from "react-big-calendar";
import { EventItem } from "./calender/types";
import Calendar from "./calender/Calender";
import AppointmentEvent, {
  DayViewColumnHeader,
} from "./calender/AppointmentEvent";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./calender/calender.scss";
import BlockoutEvent, { isDentistAbsent } from "./calender/BlockoutEvent";
import baseUrl from "../../../api/baseUrl";
import AppointmentsSchedule from "./components/AppointmentsSchedule";
import { toast } from "react-toastify";
import ReScheduleTray from "./components/ReScheduleTray";
import WalkInAppointments from "./components/WalkInAppointments";
import { useDispatch, useSelector } from "react-redux";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../redux/loader/loaderActions";
import { setTimeInterval } from "../../../redux/appointment/appointmentAction";
import { RootState } from "../../../redux/store";
import NewAppointment from "./calender/NewAppointment";
import {
  CLINIC_TIME_DURATION_CHANGED_EVENT,
  findOverlappingAppointmentEvent,
  findOverlappingOtherEvent,
  getStoredAppointmentTimeGap,
  isValidAppointmentTimeGap,
  occupiedSlotMessage,
  otherOverlapMessage,
  rangeFromCalendarSlotInfo,
  type AppointmentTimeGapMinutes,
} from "./calender/newAppointmentForm";
import {
  formatTimeHm,
  minutesBetweenTimes,
} from "./components/appointmentScheduleUi";
import OnlineAppointmentModal from "./calender/OnlineAppointmentModal";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";

type Keys = keyof typeof Views;
const STEP = 5;
const TIME_SLOTS = 1;

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function isWalkInEvent(event: any): boolean {
  const appt = event?.data?.appointment;
  if (!appt) return false;
  if (
    appt.pre_appointment_status === "walk_in" ||
    appt.preAppointmentStatus === "walk_in" ||
    appt.booking_source === "walk_in" ||
    appt.bookingSource === "walk_in"
  ) {
    return true;
  }
  return appt.status === "walkin" || appt.status === "walk_in";
}

function isOtherEvent(event: any): boolean {
  const appt = event?.data?.appointment;
  if (!appt) return false;
  const values = [
    appt.pre_appointment_status,
    appt.preAppointmentStatus,
    appt.booking_source,
    appt.bookingSource,
    appt.status,
  ].map((v) =>
    String(v ?? "")
      .trim()
      .toLowerCase()
      .replace(/-/g, "_")
  );
  return values.some(
    (v) => v === "other" || v === "call_in" || v === "callin"
  );
}

function isOffCalendarEvent(event: any): boolean {
  return isWalkInEvent(event) || isOtherEvent(event);
}

/** Same test for a raw appointment doc (tray row / reschedule target). */
function isOffCalendarAppointment(appointment: any): boolean {
  if (!appointment) return false;
  return isOffCalendarEvent({ data: { appointment } });
}

function isOtherAppointment(appointment: any): boolean {
  if (!appointment) return false;
  return isOtherEvent({ data: { appointment } });
}

function eventStartMs(event: any): number {
  const fromDate = moment(event?.start);
  if (fromDate.isValid()) return fromDate.valueOf();
  const startTime = event?.data?.appointment?.startTime;
  if (startTime) {
    const parsed = moment(startTime, ["HH:mm:ss", "HH:mm"], true);
    if (parsed.isValid()) return parsed.valueOf();
  }
  return Number.POSITIVE_INFINITY;
}

function sortEventsByStartTime(events: any[]): any[] {
  return [...events].sort((a, b) => eventStartMs(a) - eventStartMs(b));
}

function eventInCalendarView(event: any, viewDate: Date, view: string): boolean {
  const start = moment(event?.start);
  if (!start.isValid()) return false;
  if (view === Views.DAY) return start.isSame(viewDate, "day");
  if (view === Views.WEEK) {
    return (
      start.isSameOrAfter(moment(viewDate).startOf("week")) &&
      start.isSameOrBefore(moment(viewDate).endOf("week"))
    );
  }
  if (view === Views.MONTH) return start.isSame(viewDate, "month");
  return start.isSame(viewDate, "day");
}

/** Match clicked calendar time to dentist general-hours row (same logic as slot availability). */
function slotMetaForSlot(
  timeSlots: any[] | undefined,
  slotStart: Date
): { sessionName?: string; roomNumber: string } {
  const slots = timeSlots ?? [];
  const day = slotStart.getDay();
  const hours = slotStart.getHours();
  const minutes = slotStart.getMinutes();
  const match = slots.find((interval: any) => {
    const intStart = interval.start;
    const intEnd = interval.end;
    if (interval.day !== day) return false;
    const afterStart =
      hours > intStart.hours ||
      (hours === intStart.hours && minutes >= intStart.minutes);
    const beforeEnd =
      hours < intEnd.hours ||
      (hours === intEnd.hours && minutes < intEnd.minutes);
    return afterStart && beforeEnd;
  });
  if (!match) return { roomNumber: "" };
  const sessionRaw = match?.sessionName;
  const sessionName =
    sessionRaw == null ? undefined : String(sessionRaw).trim() || undefined;
  const roomRaw = match?.roomNumber;
  const roomNumber = roomRaw == null ? "" : String(roomRaw).trim();
  return { sessionName, roomNumber };
}

/** Block clinic/doctor blockouts. Hard booked/online overlap is blocked on save (walk-in/call-in skip). */
function slotOverlapsBlockout(
  events: any[],
  slotStart: Date,
  slotEnd: Date
): boolean {
  return events.some((event) => {
    if (!event?.data?.blockout) return false;
    return slotStart < event.end && slotEnd > event.start;
  });
}

/**
 * True when the doctor's absent block covers [start, end). Walk-in / Other
 * open at "now" without a slot click, so they check this before the form;
 * the server refuses any booking in absent time either way.
 */
function doctorAbsentAt(events: any[], doctorId: string, start: Date, end: Date): boolean {
  return events.some((event) => {
    const blockout = event?.data?.blockout;
    if (!blockout || !isDentistAbsent(blockout)) return false;
    if (String(event?.resourceId ?? "") !== String(doctorId)) return false;
    return start < event.end && end > event.start;
  });
}

const DOCTOR_ABSENT_NOW_MESSAGE =
  "This dentist is marked absent right now. Choose a dentist who is in.";

function durationMinutesFromAppointment(appointment: any): number {
  if (!appointment) return 30;
  const startTime = formatTimeHm(appointment?.startTime);
  const endTime = formatTimeHm(appointment?.endTime);
  const fromTimes = minutesBetweenTimes(startTime, endTime);
  const stored = parseInt(String(appointment?.interval ?? ""), 10);
  if (Number.isFinite(fromTimes) && fromTimes > 0) return fromTimes;
  if (Number.isFinite(stored) && stored > 0) return stored;
  return 30;
}

function findRescheduleAppointment(
  tray: any[],
  appointmentId: string,
  calendarEvents: any[]
): any {
  const fromTray = tray?.find(
    (item: any) => String(item?._id) === String(appointmentId)
  );
  if (fromTray) return fromTray;
  const fromEvent = calendarEvents?.find(
    (event: any) =>
      String(event?.data?.appointment?.id ?? event?.data?.appointment?._id) ===
      String(appointmentId)
  );
  return fromEvent?.data?.appointment;
}

function rescheduleEndFromStart(slotStart: Date, appointment: any): Date {
  const end = new Date(slotStart);
  end.setMinutes(end.getMinutes() + durationMinutesFromAppointment(appointment));
  return end;
}

function appointmentsOverlap(first: any, second: any) {
  if (!first?.start || !first?.end || !second?.start || !second?.end) return false;
  return first.start < second.end && second.start < first.end;
}

function toResourceId(value: any): string | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value === "object") {
    const nested = value._id ?? value.id;
    if (nested != null && nested !== "") return String(nested);
    return undefined;
  }
  const s = String(value).trim();
  return !s || s === "[object Object]" ? undefined : s;
}

/** Booked/online calendar events that occupy a hard slot (walk-in/call-in/cancel ignored). */
function isHardCalendarBookingEvent(event: any): boolean {
  if (!event?.data?.appointment || event?.data?.blockout) return false;
  // Anything the walk-in/other trays claim (incl. legacy status walkin/callin
  // rows without pre_appointment_status) never occupies the hard calendar.
  if (isOffCalendarEvent(event)) return false;
  const apt = event.data.appointment;
  const pas = String(
    apt.pre_appointment_status ?? apt.preAppointmentStatus ?? apt.booking_source ?? ""
  )
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (
    pas === "walk_in" ||
    pas === "walkin" ||
    pas === "other" ||
    pas === "call_in" ||
    pas === "callin" ||
    pas === "cancel" ||
    pas === "cancelled" ||
    pas === "canceled"
  ) {
    return false;
  }
  const st = String(apt.status ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  if (st === "cancelled" || st === "canceled" || st === "none" || st === "cancel") {
    return false;
  }
  return true;
}

function eventMatchesDoctor(event: any, doctorId: string): boolean {
  const key = String(doctorId ?? "").trim();
  if (!key || key === "all") return true;
  const eventDoctor =
    toResourceId(event?.resourceId) ||
    toResourceId(event?.data?.appointment?.doctorId);
  return !eventDoctor || eventDoctor === key;
}

/** True when slotStart falls inside an existing hard booking [start, end). */
function findHardBookingCoveringSlot(
  events: any[],
  slotStart: Date,
  doctorId: string
): any | null {
  const t = slotStart.getTime();
  for (const event of events ?? []) {
    if (!isHardCalendarBookingEvent(event)) continue;
    if (!eventMatchesDoctor(event, doctorId)) continue;
    const start = new Date(event.start).getTime();
    const end = new Date(event.end).getTime();
    if (Number.isFinite(start) && Number.isFinite(end) && start <= t && t < end) {
      return event;
    }
  }
  return null;
}

/** Earliest hard booking that starts strictly after slotStart. */
function findNextHardBookingStart(
  events: any[],
  slotStart: Date,
  doctorId: string
): Date | null {
  const t = slotStart.getTime();
  let next: Date | null = null;
  for (const event of events ?? []) {
    if (!isHardCalendarBookingEvent(event)) continue;
    if (!eventMatchesDoctor(event, doctorId)) continue;
    const start = new Date(event.start);
    if (Number.isNaN(start.getTime()) || start.getTime() <= t) continue;
    if (!next || start.getTime() < next.getTime()) next = start;
  }
  return next;
}

function formatOverlapRangeToast(event: any): string {
  const start = moment(event?.start).isValid()
    ? moment(event.start).format("HH:mm")
    : "?";
  const end = moment(event?.end).isValid()
    ? moment(event.end).format("HH:mm")
    : "?";
  return `This time overlaps another appointment (${start}–${end}). Choose a free slot or a shorter duration.`;
}

function overlapResourceKey(event: any) {
  return (
    toResourceId(event?.resourceId) ||
    toResourceId(event?.data?.appointment?.doctorId) ||
    "unassigned"
  );
}

function addOverlapMeta(events: any[]) {
  return events.map((event, index) => {
    if (!event?.data?.appointment) return event;

    const overlapCount = events.reduce((count, candidate, candidateIndex) => {
      if (candidateIndex === index || !candidate?.data?.appointment) return count;
      if (overlapResourceKey(candidate) !== overlapResourceKey(event)) return count;
      return appointmentsOverlap(event, candidate) ? count + 1 : count;
    }, 0);

    return {
      ...event,
      data: {
        ...event.data,
        appointment: {
          ...event.data.appointment,
          _overlapCount: overlapCount,
        },
      },
    };
  });
}

function fullWidthDayLayout({ events, accessors, slotMetrics }: any) {
  return events.map((event: any) => {
    const { top, height } = slotMetrics.getRange(
      accessors.start(event),
      accessors.end(event)
    );

    return {
      event,
      style: {
        top,
        height,
        width: "100%",
        xOffset: 0,
      },
    };
  });
}

export default function MakeAppointment() {
  const dispatch = useDispatch();
  const [date, setDate] = useState<Date>(moment().toDate());
  const [view, setView] = useState<(typeof Views)[Keys]>(Views.DAY);
  const [periodMode, setPeriodMode] = useState<"today" | "all">("today");
  const [appointmentDetail, setAppointmentDetail] = useState({});
  const [events, setEvents] = useState<any[]>([]);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [tray, setTray] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState("all");
  const [selectReSchedule, setSelectReSchedule] = useState("");
  const [appointmentType, setAppointmentType] = useState("schedule");
  const [appointmentModal, setAppointmentModal] = useState(false);
  const [newAppointmentModal, setNewAppointmentModal] = useState(false);
  const [newappointmentDetail, setNewAppointmentDetail] = useState({});
  const [onlineAppointmentModal, setOnlineAppointmentModal] = useState(false);
  const [onlineAppointmentDetail, setOnlineAppointmentDetail] = useState({});
  const [timeGapMinutes, setTimeGapMinutes] = useState<AppointmentTimeGapMinutes>(
    () => getStoredAppointmentTimeGap()
  );

  useEffect(() => {
    const onDurationChanged = (event: Event) => {
      const gap = (event as CustomEvent<number>).detail;
      if (isValidAppointmentTimeGap(gap)) setTimeGapMinutes(gap);
    };
    window.addEventListener(CLINIC_TIME_DURATION_CHANGED_EVENT, onDurationChanged);
    return () => {
      window.removeEventListener(
        CLINIC_TIME_DURATION_CHANGED_EVENT,
        onDurationChanged
      );
    };
  }, []);

  const formatTime = useCallback((dates?: any) => {
    const date = new Date(dates);
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}:${String(date.getSeconds()).padStart(2, "0")}`;
  }, []);

  const newInterval = useSelector((state: RootState) => state.appointment);
  const onlineappointment = useSelector(
    (state: RootState) => state.makeappointment.onlineappointment
  );

  const calendarEvents = useMemo(
    () => addOverlapMeta(events.filter((event) => !isOffCalendarEvent(event))),
    [events]
  );

  // All loaded Other bookings (any day in range) for the Other-vs-Other check.
  const allOtherEvents = useMemo(() => events.filter(isOtherEvent), [events]);

  const walkInEvents = useMemo(
    () =>
      sortEventsByStartTime(
        events.filter(
          (event) =>
            isWalkInEvent(event) && eventInCalendarView(event, date, view)
        )
      ),
    [events, date, view]
  );

  const otherEvents = useMemo(
    () =>
      sortEventsByStartTime(
        events.filter(
          (event) =>
            isOtherEvent(event) && eventInCalendarView(event, date, view)
        )
      ),
    [events, date, view]
  );

  const showDayColumnHeader = view === Views.DAY && doctor !== "all";

  const components: any = {
    event: ({ event }: EventProps<EventItem>) => {
      const data = event?.data;

      if (data?.appointment) {
        // Attach event data to appointment for date/time extraction
        const appointmentWithEvent = {
          ...data?.appointment,
          _eventStart: event?.start,
          _eventEnd: event?.end,
        };
        return (
          <AppointmentEvent
            reschedule={(e: any) => reschedule(e)}
            appointment={appointmentWithEvent}
            isMonthView={view === Views.MONTH}
            isDayView={view === Views.DAY}
            colorByPurpose={doctor !== "all"}
            hideDoctorName={view === Views.DAY}
            alignColumns={showDayColumnHeader}
            onOnlineAppointmentClick={handleOnlineAppointmentClick}
          />
        );
      }
      if (data?.blockout) {
        return <BlockoutEvent blockout={data?.blockout} />;
      }
      return null;
    },
    ...(showDayColumnHeader
      ? {
          resourceHeader: ({ label }: { label?: React.ReactNode }) => (
            <div className="w-full min-w-0">
              <div className="px-3 py-2 text-center text-sm font-semibold text-slate-700 bg-sky-50/80 border-b border-slate-200/70">
                {label}
              </div>
              <DayViewColumnHeader />
            </div>
          ),
        }
      : {}),
  };

  const fetchAppointments = useCallback((doctorId: any, centerDate?: Date) => {
    const d = centerDate || date;
    const start = moment(d).subtract(1, "month").startOf("day").format("YYYY-MM-DD");
    const end = moment(d).add(1, "month").endOf("day").format("YYYY-MM-DD");
    dispatch(setloadeActive());
    baseUrl
      .get("/appointment/findAllAppointmentsByDoctor", {
        params: { startDate: start, endDate: end },
        headers: doctorId === "all" ? {} : { doctorid: doctorId },
      })
      .then(async (response) => {
        const data = response.data.data
          .map((element: any) => {
            const resourceId = toResourceId(
              element.resourceId ??
                element.data?.appointment?.doctorId ??
                element.doctorId
            );
            return {
              ...element,
              start: moment(element.start).toDate(),
              end: moment(element.end).toDate(),
              resourceId: resourceId != null ? String(resourceId) : undefined,
            };
          })
          .sort(
            (a: any, b: any) =>
              moment(a.start).valueOf() - moment(b.start).valueOf()
          );
        setTimeSlots(
          response.data.doctorWorkingHours ??
            response.data.workingGeneralHours ??
            []
        );
        setEvents(data);
        dispatch(setloadeDeactive());
      })
      .catch((error) => {
        console.error(error);
      });
  }, [dispatch, date]);

  const fetchTrayAppointments = useCallback(() => {
    dispatch(setloadeActive());
    baseUrl
      .get("/appointment/findAllAppointments", {
        headers: { status: "tray" },
      })
      .then((response) => {
        setTray(response.data.data);
        dispatch(setloadeDeactive());
      })
      .catch((error) => {
        console.error(error);
      });
  }, [dispatch]);

  useEffect(() => {
    // Handle online appointment updates
    if (Object.keys(onlineappointment).length > 0) {
      fetchAppointments(doctor);
      fetchTrayAppointments();
    }
  }, [onlineappointment, doctor, fetchAppointments, fetchTrayAppointments]);

  const timrInterval = useCallback(() => {
    baseUrl
      .get("/clinic/getTimeData")
      .then((response) => {
        if (response.data.data.startTime) {
          dispatch(
            setTimeInterval(
              {
                from: moment(
                  `2022-10-10T${response.data.data.startTime}`
                ).toDate(),
                to: moment(`2022-10-10T${response.data.data.endTime}`).toDate(),
              },
              false
            )
          );
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [dispatch]);

  useEffect(() => {
    fetchTrayAppointments();
    fetchAppointments(doctor);
    if (newInterval?.intervalIsEMpty) timrInterval();

    baseUrl
      .get("/appointment/findAllAppointmentDentist")
      .then((response) => {
        setDoctors(response.data.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [doctor, fetchAppointments, fetchTrayAppointments, newInterval, timrInterval]);

  /**
   * Schedule rows (session + room per half hour) are cached from the fetch
   * above. Refetch when a session is created/edited/moved/deleted so a tab
   * left open never books with a stale room.
   */
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onScheduleChange = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fetchAppointments(doctor), 250);
    };
    socket.on("dentistScheduleUpdate", onScheduleChange);
    socket.on("roomAllocationUpdate", onScheduleChange);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off("dentistScheduleUpdate", onScheduleChange);
      socket.off("roomAllocationUpdate", onScheduleChange);
    };
  }, [doctor, fetchAppointments]);

  const handleOnlineAppointmentClick = useCallback(
    (appointment: any) => {
      // Extract date from event start date or appointment date
      let appointmentDate = appointment?.date;
      if (!appointmentDate && appointment?._eventStart) {
        appointmentDate = formatDate(appointment._eventStart);
      }
      if (!appointmentDate) {
        appointmentDate = formatDate(new Date());
      }

      // Extract time from appointment or event
      let startTime = appointment?.startTime;
      let endTime = appointment?.endTime;
      if (!startTime && appointment?._eventStart) {
        startTime = formatTime(appointment._eventStart);
      }
      if (!endTime && appointment?._eventEnd) {
        endTime = formatTime(appointment._eventEnd);
      }

      // Clean up the appointment object (remove internal event fields)
      const { _eventStart, _eventEnd, ...cleanAppointment } = appointment;

      setOnlineAppointmentDetail({
        ...cleanAppointment,
        id: cleanAppointment?.id, // Store appointment ID for updating
        date: appointmentDate,
        startTime: startTime || cleanAppointment?.startTime || "",
        endTime: endTime || cleanAppointment?.endTime || "",
        doctorId: cleanAppointment?.doctorId || doctor,
      });
      setOnlineAppointmentModal(true);
    },
    [doctor, formatTime]
  );

  const reschedule = useCallback(
    (e: any) => {
      if (e.type === "online" && !e.added) {
        handleOnlineAppointmentClick(e);
      } else {
        if (selectReSchedule !== "") {
          toast.info(
            "Click an empty time on the calendar to set the new appointment time.",
            { autoClose: 5000 }
          );
          return;
        } else {
          const appointmentId = e?.id ?? e?._id;
          if (!appointmentId) {
            toast.error("Could not open this appointment.");
            return;
          }
          setAppointmentType("Completed");
          setAppointmentDetail({
            action: "Completed",
            appointmentId,
            isCompleted: e?.status === "completed",
          });
          setAppointmentModal(true);
        }
      }
    },
    [selectReSchedule, handleOnlineAppointmentClick]
  );

  // Walk-in / Other never occupy a slot, so a booked appointment must not stop
  // staff picking that time for one. The booking type lives in the side panel,
  // which opens before the slot is clicked, hence the flag.
  const pendingSoftBooking = Boolean(
    newAppointmentModal &&
      ((newappointmentDetail as any)?.walkIn ||
        (newappointmentDetail as any)?.other)
  );

  const makeAppointment = useCallback(
    (e: any) => {
      // Require a doctor to be selected (or in day view, slot must have resourceId)
      if (doctor === "all") {
        toast.warning("Please select a doctor.");
        return;
      }

      // If tray has appointments, ONLY allow rescheduling - block new appointments
      if (tray && tray.length > 0) {
        // Must select from reschedule tray first
        if (!selectReSchedule) {
          toast.info(
            "Step 1: Select a patient in the reschedule tray above, then click an empty time on the calendar.",
            { autoClose: 6000 }
          );
          return;
        }

        const now = new Date();
        const isPastDate = e.start.getTime() < now.getTime();

        if (isPastDate) {
          toast("You cannot make an appointment before now");
          return;
        }

        const startDayOfWeek = e.start.getDay();
        const startHours = e.start.getHours();
        const startMinutes = e.start.getMinutes();
        const isClickable = (timeSlots ?? []).some((interval: any) => {
          const intStart = interval.start;
          const intEnd = interval.end;
          return (
            interval.day === startDayOfWeek &&
            (startHours > intStart.hours ||
              (startHours === intStart.hours &&
                startMinutes >= intStart.minutes)) &&
            (startHours < intEnd.hours ||
              (startHours === intEnd.hours && startMinutes < intEnd.minutes))
          );
        });

        if (!isClickable) {
          toast("Selected time is not available.");
          return;
        }

        const selectedAppt = findRescheduleAppointment(tray, selectReSchedule, events);
        const slotEnd = rescheduleEndFromStart(e.start, selectedAppt);

        if (slotOverlapsBlockout(events, e.start, slotEnd)) {
          toast("This time slot is blocked.");
          return;
        }

        const trayDoctorId = view === "day" ? e.resourceId : doctor;
        const trayRange = {
          start: e.start,
          end: slotEnd,
          doctorId: trayDoctorId,
          excludeAppointmentId: selectReSchedule,
        };
        if (isOtherAppointment(selectedAppt)) {
          const otherClash = findOverlappingOtherEvent(events, trayRange);
          if (otherClash) {
            toast.error(otherOverlapMessage(otherClash));
            return;
          }
        } else if (!isOffCalendarAppointment(selectedAppt)) {
          const trayOverlap = findOverlappingAppointmentEvent(events, trayRange);
          if (trayOverlap) {
            toast.error(occupiedSlotMessage(trayOverlap));
            return;
          }
        }

        // Allow rescheduling when appointment is selected from tray
        setAppointmentType("Reschedule");
        const rescheduleMeta = slotMetaForSlot(timeSlots, e.start);
        setAppointmentDetail({
          ...e,
          action: "Reschedule",
          start: formatDate(new Date(e.start)),
          end: formatDate(slotEnd),
          startTime: formatTime(e.start),
          endTime: formatTime(slotEnd),
          appointmentId: selectReSchedule,
          doctorId: view === "day" ? e.resourceId : doctor,
          fromRescheduleTray: true,
          slotPickedOnCalendar: true,
          ...(rescheduleMeta.sessionName
            ? { sessionName: rescheduleMeta.sessionName }
            : {}),
          ...(rescheduleMeta.roomNumber
            ? { roomNumber: rescheduleMeta.roomNumber }
            : {}),
        });
        setAppointmentModal(true);
        return; // Exit early - don't allow new appointments
      }

      // Tray is empty, allow normal flow (reschedule or new appointment)
      const now = new Date();
      const isPastDate = e.start.getTime() < now.getTime();

      if (isPastDate) {
        toast("You cannot make an appointment before now");
        return;
      }

      const startDayOfWeek = e.start.getDay();
      const startHours = e.start.getHours();
      const startMinutes = e.start.getMinutes();
      const isClickable = (timeSlots ?? []).some((interval: any) => {
        const intStart = interval.start;
        const intEnd = interval.end;
        return (
          interval.day === startDayOfWeek &&
          (startHours > intStart.hours ||
            (startHours === intStart.hours &&
              startMinutes >= intStart.minutes)) &&
          (startHours < intEnd.hours ||
            (startHours === intEnd.hours && startMinutes < intEnd.minutes))
        );
      });

      if (!isClickable) {
        toast("Selected time is not available.");
        return;
      }

      if (selectReSchedule) {
        const selectedAppt = findRescheduleAppointment(tray, selectReSchedule, events);
        const slotEnd = rescheduleEndFromStart(e.start, selectedAppt);

        if (slotOverlapsBlockout(events, e.start, slotEnd)) {
          toast("This time slot is blocked.");
          return;
        }

        const rescheduleDoctorId = view === "day" ? e.resourceId : doctor;
        const rescheduleRange = {
          start: e.start,
          end: slotEnd,
          doctorId: rescheduleDoctorId,
          excludeAppointmentId: selectReSchedule,
        };
        if (isOtherAppointment(selectedAppt)) {
          const otherClash = findOverlappingOtherEvent(events, rescheduleRange);
          if (otherClash) {
            toast.error(otherOverlapMessage(otherClash));
            return;
          }
        } else if (!isOffCalendarAppointment(selectedAppt)) {
          const rescheduleOverlap = findOverlappingAppointmentEvent(
            events,
            rescheduleRange
          );
          if (rescheduleOverlap) {
            toast.error(occupiedSlotMessage(rescheduleOverlap));
            return;
          }
        }

        setAppointmentType("Reschedule");
        const rescheduleMeta = slotMetaForSlot(timeSlots, e.start);
        setAppointmentDetail({
          ...e,
          action: "Reschedule",
          start: formatDate(new Date(e.start)),
          end: formatDate(slotEnd),
          startTime: formatTime(e.start),
          endTime: formatTime(slotEnd),
          appointmentId: selectReSchedule,
          doctorId: view === "day" ? e.resourceId : doctor,
          fromRescheduleTray: true,
          slotPickedOnCalendar: true,
          ...(rescheduleMeta.sessionName
            ? { sessionName: rescheduleMeta.sessionName }
            : {}),
          ...(rescheduleMeta.roomNumber
            ? { roomNumber: rescheduleMeta.roomNumber }
            : {}),
        });
        setAppointmentModal(true);
      } else {
        // Allow making new appointment only when tray is empty (doctor required unless day view with column)
        if (doctor === "all" && (view !== Views.DAY || !e?.resourceId)) {
          toast.warning("Please select a doctor.");
          return;
        }

        setAppointmentType("schedule");

        const doctorIdForSlot =
          view === "day" ? e.resourceId : doctor;
        const { start: slotStart, end: proposedEnd } = rangeFromCalendarSlotInfo(
          e,
          { stepMinutes: STEP }
        );

        let slotEnd = proposedEnd;
        // Hard bookings only: a walk-in / Other may sit on top of a booked slot
        // and keeps the clicked duration.
        if (!pendingSoftBooking) {
          const covering = findHardBookingCoveringSlot(
            events,
            slotStart,
            String(doctorIdForSlot ?? "")
          );
          if (covering) {
            toast.error(formatOverlapRangeToast(covering));
            return;
          }

          // Default click is 30 min; clamp so we open the form when a shorter
          // gap exists before the next booked/online appointment (e.g. 09:50→10:00).
          const nextHardStart = findNextHardBookingStart(
            events,
            slotStart,
            String(doctorIdForSlot ?? "")
          );
          if (nextHardStart && slotEnd.getTime() > nextHardStart.getTime()) {
            slotEnd = new Date(nextHardStart);
          }
          if (slotEnd.getTime() <= slotStart.getTime()) {
            toast.error(
              "No free time before the next appointment. Choose an earlier slot."
            );
            return;
          }
        }

        if (slotOverlapsBlockout(events, slotStart, slotEnd)) {
          toast("This time slot is blocked.");
          return;
        }

        const slotMeta = slotMetaForSlot(timeSlots, slotStart);
        setNewAppointmentDetail({
          action: "Schedule",
          start: slotStart,
          end: slotEnd,
          slots: e.slots,
          startTime: formatTime(slotStart),
          endTime: formatTime(slotEnd),
          date: formatDate(slotStart),
          doctorId: doctorIdForSlot,
          ...(slotMeta.sessionName ? { sessionName: slotMeta.sessionName } : {}),
          ...(slotMeta.roomNumber ? { roomNumber: slotMeta.roomNumber } : {}),
          // Panel resets its form from this object, so the mode must survive.
          ...(pendingSoftBooking && (newappointmentDetail as any)?.walkIn
            ? { walkIn: true }
            : {}),
          ...(pendingSoftBooking && (newappointmentDetail as any)?.other
            ? { other: true }
            : {}),
        });
        setNewAppointmentModal(true);
      }
    },
    [
      doctor,
      selectReSchedule,
      timeSlots,
      view,
      events,
      tray,
      formatTime,
      pendingSoftBooking,
      newappointmentDetail,
    ]
  );

  const onSave = useCallback(() => {
    fetchAppointments(doctor);
    fetchTrayAppointments();
    // Don't clear selectReSchedule here - it will be cleared after successful reschedule
    // setSelectReSchedule("");
  }, [doctor, fetchAppointments, fetchTrayAppointments]);

  const onDoctorSelected = useCallback(
    (e: any) => {
      setDoctor(e.target.value);
      fetchAppointments(e.target.value);
    },
    [fetchAppointments]
  );

  const handleWalkIn = useCallback(() => {
    if (doctor === "all") {
      toast.warning("Please select a doctor.");
      return;
    }

    const now = new Date();
    const slotEnd = new Date(now);
    slotEnd.setMinutes(slotEnd.getMinutes() + timeGapMinutes);
    if (doctorAbsentAt(events, doctor, now, slotEnd)) {
      toast.error(DOCTOR_ABSENT_NOW_MESSAGE);
      return;
    }
    const slotMeta = slotMetaForSlot(timeSlots, now);

    setNewAppointmentDetail({
      action: "Schedule",
      startTime: formatTime(now),
      endTime: formatTime(slotEnd),
      date: formatDate(now),
      doctorId: doctor,
      walkIn: true,
      ...(slotMeta.sessionName ? { sessionName: slotMeta.sessionName } : {}),
      ...(slotMeta.roomNumber ? { roomNumber: slotMeta.roomNumber } : {}),
    });
    setNewAppointmentModal(true);
  }, [doctor, events, formatTime, timeSlots, timeGapMinutes]);

  const handleOther = useCallback(() => {
    if (doctor === "all") {
      toast.warning("Please select a doctor.");
      return;
    }

    const now = new Date();
    const slotEnd = new Date(now);
    slotEnd.setMinutes(slotEnd.getMinutes() + timeGapMinutes);
    if (doctorAbsentAt(events, doctor, now, slotEnd)) {
      toast.error(DOCTOR_ABSENT_NOW_MESSAGE);
      return;
    }
    const slotMeta = slotMetaForSlot(timeSlots, now);

    setNewAppointmentDetail({
      action: "Schedule",
      startTime: formatTime(now),
      endTime: formatTime(slotEnd),
      date: formatDate(now),
      doctorId: doctor,
      other: true,
      ...(slotMeta.sessionName ? { sessionName: slotMeta.sessionName } : {}),
      ...(slotMeta.roomNumber ? { roomNumber: slotMeta.roomNumber } : {}),
    });
    setNewAppointmentModal(true);
  }, [doctor, events, formatTime, timeSlots, timeGapMinutes]);

  const dayPropGetter = useCallback(
    (date: Date) => {
      const dayOfWeek = date.getDay();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const isTenMinute = minutes % 10 === 0;
      const isBlocked = (timeSlots ?? []).some((interval: any) => {
        const start = interval.start;
        const end = interval.end;
        return (
          interval.day === dayOfWeek &&
          (hours > start.hours ||
            (hours === start.hours && minutes >= start.minutes)) &&
          (hours < end.hours || (hours === end.hours && minutes < end.minutes))
        );
      });

      return isBlocked
        ? {
            className: isTenMinute
              ? "calendar-slot-available calendar-slot-ten-minute"
              : "calendar-slot-available",
            style: { cursor: "pointer" },
          }
        : {
            className: isTenMinute
              ? "calendar-slot-unavailable calendar-slot-ten-minute"
              : "calendar-slot-unavailable",
            style: { cursor: "no-drop" },
          };
    },
    [timeSlots]
  );

  const slotGroupPropGetter = useCallback((group?: Date[]) => {
    const slot = Array.isArray(group) ? group[0] : undefined;
    if (!slot || new Date(slot).getMinutes() % 30 !== 0) {
      return { className: "rbc-timeslot-group" } as React.HTMLAttributes<HTMLDivElement>;
    }
    return {
      className: "rbc-timeslot-group calendar-half-hour",
    } as React.HTMLAttributes<HTMLDivElement>;
  }, []) as SlotGroupPropGetter;

  const eventPropGetter = useCallback((event: any) => {
    if (event?.data?.appointment?.status === "completed") {
      return {
        className: "calendar-event-completed",
        style: {
          backgroundColor: "#fce7f3",
          borderColor: "#f9a8d4",
        },
      };
    }
    return {
      style: {
        backgroundColor: "transparent",
        borderColor: "transparent",
      },
    };
  }, []);

  return (
    <div
      className={`flex flex-col lg:flex-row gap-4 transition-[padding] duration-200 ${
        newAppointmentModal ? "lg:pr-[26rem]" : ""
      }`}
    >
      {/* Calendar Section */}
      <div className="flex-1 min-w-0 bg-white rounded-xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
        {/* Header Controls */}
        
        <div className="p-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="relative z-20 flex items-center gap-2">
              <CalendarTodayIcon
                className="text-primary"
                sx={{ fontSize: 20 }}
              />
              <div className="[&_.react-datepicker-wrapper]:w-auto [&_input]:px-4 [&_input]:py-2 [&_input]:bg-white [&_input]:border [&_input]:border-slate-200 [&_input]:rounded-xl [&_input]:outline-none [&_input]:focus:ring-2 [&_input]:focus:ring-primary [&_input]:focus:border-transparent [&_input]:transition-all [&_input]:text-slate-700 [&_input]:font-medium">
                <DatePicker
                  selected={date}
                  onChange={(newDate: any) => {
                    setPeriodMode("all");
                    setDate(newDate);
                  }}
                />
              </div>
              <div className="flex h-[42px] items-center gap-1 rounded-xl bg-slate-100 p-1">
                <button
                  type="button"
                  onClick={() => {
                    setPeriodMode("today");
                    setDate(moment().toDate());
                    setView(Views.DAY);
                  }}
                  className={`h-full rounded-lg px-3 text-sm font-medium transition-all whitespace-nowrap ${
                    periodMode === "today"
                      ? "bg-white text-primary shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPeriodMode("all");
                    setView(Views.WEEK);
                  }}
                  className={`h-full rounded-lg px-3 text-sm font-medium transition-all whitespace-nowrap ${
                    periodMode === "all"
                      ? "bg-white text-primary shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  All
                </button>
              </div>
            </div>
            <div className="text-2xl font-bold">Make An Appointment</div>
            {/* Doctor Select */}
            <div className="flex items-center gap-2">
              <PersonIcon className="text-primary" sx={{ fontSize: 20 }} />
              <select
                name="selectdoctor"
                value={doctor}
                onChange={(e: any) => onDoctorSelected(e)}
                className="px-4 py-2 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-slate-700 font-medium min-w-[180px]"
              >
                <option value="all">All Doctors</option>
                {doctors.map((item: any, i: number) => (
                  <option key={i} value={item?.id}>
                    Dr. {item?.title + " " + item?.lastName}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleWalkIn}
                className="px-4 py-2 bg-primary text-white rounded-xl font-medium hover:bg-primary/90 transition-colors whitespace-nowrap"
              >
                Walk in
              </button>
              <button
                type="button"
                onClick={handleOther}
                className="px-4 py-2 bg-sky-600 text-white rounded-xl font-medium hover:bg-sky-700 transition-colors whitespace-nowrap"
              >
                Other
              </button>
            </div>
          </div>
        </div>
        {/* Grid Layout: ReScheduleTray at top, Calendar below */}
        <div className="grid grid-cols-1 gap-4 p-1">
          {/* Reschedule Tray Section */}
          {tray.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden">
              <ReScheduleTray
                data={tray}
                selectedId={selectReSchedule}
                setSelectReSchedule={setSelectReSchedule}
                onCancel={fetchTrayAppointments}
              />
            </div>
          )}
          
          {/* Calendar */}
          <div
            className={`overflow-auto [&_.rbc-timeslot-group]:min-h-[30px] ${
              showDayColumnHeader ? "day-view-columns" : ""
            }`}
          >
            <Calendar
              events={calendarEvents}
              defaultDate={"2022-10-10"}
              defaultView={Views.DAY}
              resources={
                view === Views.DAY
                  ? (doctor === "all"
                      ? doctors.map((d: any) => ({
                          id: String(d?.id ?? d?._id),
                          title: `Dr. ${d?.title ?? ""} ${d?.lastName ?? ""}`.trim(),
                        }))
                      : doctors
                          .filter((d: any) => String(d?.id ?? d?._id) === String(doctor))
                          .map((d: any) => ({
                            id: String(d?.id ?? d?._id),
                            title: `Dr. ${d?.title ?? ""} ${d?.lastName ?? ""}`.trim(),
                          })))
                  : undefined
              }
              min={newInterval?.interval?.from}
              max={newInterval?.interval?.to}
              onSelectSlot={view !== Views.MONTH ? makeAppointment : undefined}
              components={view !== Views.MONTH ? components : undefined}
              dayLayoutAlgorithm={view === Views.DAY ? fullWidthDayLayout : "overlap"}
              slotPropGetter={dayPropGetter}
              slotGroupPropGetter={slotGroupPropGetter}
              eventPropGetter={eventPropGetter}
              startAccessor="start"
              endAccessor="end"
              date={date}
              view={view}
              onView={setView}
              onNavigate={setDate}
              step={STEP}
              timeslots={TIME_SLOTS}
              formats={{ timeGutterFormat: "HH:mm" }}
              selectable
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-4">
            <WalkInAppointments
              items={walkInEvents}
              colorByPurpose={doctor !== "all"}
              onSelect={reschedule}
              variant="walkin"
            />
            <WalkInAppointments
              items={otherEvents}
              colorByPurpose={doctor !== "all"}
              onSelect={reschedule}
              variant="other"
            />
          </div>
        </div>
        {appointmentModal && (
          <AppointmentsSchedule
            doctor={doctors}
            appointmentDetail={appointmentDetail}
            appointmentType={appointmentType}
            setappointmentModal={setAppointmentModal}
            onsave={onSave}
            selectReSchedule={selectReSchedule}
            setSelectReSchedule={setSelectReSchedule}
            timeSlots={timeSlots}
            events={calendarEvents}
            otherEvents={allOtherEvents}
            tray={tray}
            setAppointmentDetail={setAppointmentDetail}
            setAppointmentType={setAppointmentType}
          />
        )}
        {newAppointmentModal && (
          <NewAppointment
            newappointmentDetail={newappointmentDetail}
            setNewAppointmentModal={setNewAppointmentModal}
            doctor={doctors}
            onsave={onSave}
            timeGapMinutes={timeGapMinutes}
            timeSlots={timeSlots}
            events={calendarEvents}
            otherEvents={allOtherEvents}
          />
        )}
        {onlineAppointmentModal && (
          <OnlineAppointmentModal
            onlineAppointment={onlineAppointmentDetail}
            setOnlineAppointmentModal={setOnlineAppointmentModal}
            doctor={doctors}
            onsave={onSave}
          />
        )}
      </div>
      
    </div>
  );
}
